import { type ClassValue, clsx } from 'clsx'
import { useContext } from 'react'
import { extendTailwindMerge } from 'tailwind-merge'

// Extend tailwind-merge to understand custom design system
// https://stackoverflow.com/a/79231957/12988774
//
// By default, tailwind-merge groups classes that affect the same CSS property to prevent conflicts.
// For example, 'text-red-500' and 'text-blue-500' are in the same group because they both set color.
// When merging classes, tailwind-merge keeps only the last class from each group.
//
// However, our custom classes like 'text-reg' (font-size) and 'text-text-primary' (color) use the
// same 'text-' prefix but control different CSS properties. Without this configuration, tailwind-merge
// would incorrectly group them together and remove one when merging, even though they don't conflict.
//
// By defining separate classGroups, we tell tailwind-merge that:
// - 'text-reg', 'text-md', etc. only affect font-size
// - 'text-text-primary', 'text-text-secondary', etc. only affect color
// - These groups don't conflict with each other, so both classes can coexist
//
// This ensures that cn('text-reg text-text-primary') keeps both classes instead of dropping one.

const customTwMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      // Custom font sizes (typography variants)
      'font-size': [
        'text-xs',
        'text-sm',
        'text-reg',
        'text-md',
        'text-lg',
        'text-xl',
        'text-2xl',
        'text-3xl',
        'text-4xl',
      ],
      // Custom text colors (separate from font-size)
      'text-color': [
        'text-text-primary',
        'text-text-secondary',
        'text-text-tertiary',
        'text-text-primary-contrast',
        'text-text-secondary-contrast',
      ],
    },
    // Ensure these groups don't conflict with each other
    conflictingClassGroups: {
      'font-size': [], // font-size doesn't conflict with text-color
      'text-color': [], // text-color doesn't conflict with font-size
    },
  },
})

export function cn(...inputs: ClassValue[]): string {
  return customTwMerge(clsx(inputs))
}

export function useContextSafely<T>(context: React.Context<T>): NonNullable<T> {
  const value = useContext(context)
  if (value == null) {
    throw new Error(`${context.displayName} cannot be used outside of provider`)
  }
  return value as NonNullable<T>
}
